// Lightweight client-side image QA utilities.
// dHash (difference hash): downscale to 9x8 grayscale, compare adjacent pixels
// to produce a 64-bit fingerprint. Hamming distance between two fingerprints
// approximates perceptual distance (0 = identical, 64 = maximally different).

export type ImageStats = {
  width: number;
  height: number;
  bytes: number; // exact byte size of fetched blob
  hash: bigint;
};

async function loadImage(src: string): Promise<HTMLImageElement> {
  return await new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("image load failed"));
    img.src = src;
  });
}

function computeDHash(img: HTMLImageElement): bigint {
  const w = 9;
  const h = 8;
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) return 0n;
  ctx.drawImage(img, 0, 0, w, h);
  const data = ctx.getImageData(0, 0, w, h).data;
  // grayscale
  const gray = new Uint8Array(w * h);
  for (let i = 0; i < w * h; i++) {
    const r = data[i * 4];
    const g = data[i * 4 + 1];
    const b = data[i * 4 + 2];
    gray[i] = Math.round(0.299 * r + 0.587 * g + 0.114 * b);
  }
  let hash = 0n;
  let bit = 0n;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w - 1; x++) {
      const left = gray[y * w + x];
      const right = gray[y * w + x + 1];
      if (left > right) hash |= 1n << bit;
      bit++;
    }
  }
  return hash;
}

export async function getImageStats(src: string): Promise<ImageStats> {
  // Fetch as blob to get true bytes; data URLs work too.
  const res = await fetch(src);
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  try {
    const img = await loadImage(objectUrl);
    const hash = computeDHash(img);
    return { width: img.naturalWidth, height: img.naturalHeight, bytes: blob.size, hash };
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export function hamming64(a: bigint, b: bigint): number {
  let x = a ^ b;
  let c = 0;
  while (x !== 0n) {
    c += Number(x & 1n);
    x >>= 1n;
  }
  return c;
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}
